import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LoginIcon from "@mui/icons-material/Login";
import { useTranslation } from "react-i18next";
import Seo from "../../seo/Seo";

export default function PublicNotFound() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { t: tSeo } = useTranslation("seo");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 50%, ${colors.blueAccent[900]} 100%)`,
        p: 3,
      }}
    >
      <Seo
        title={tSeo("notFound.title")}
        description={tSeo("notFound.description")}
        keywords={tSeo("notFound.keywords")}
        noindex
      />
      <Container maxWidth="lg" sx={{ width: "100%", maxWidth: "1200px" }}>
        <Box
          sx={{
            textAlign: "center",
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            borderRadius: "24px",
            p: 6,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
            border: `2px solid ${colors.blueAccent[500]}40`,
            width: "100%",
          }}
        >
          {/* Error Icon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[700]} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 8px 24px ${colors.redAccent[500]}40`,
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": {
                    transform: "scale(1)",
                    boxShadow: `0 8px 24px ${colors.redAccent[500]}40`,
                  },
                  "50%": {
                    transform: "scale(1.05)",
                    boxShadow: `0 12px 32px ${colors.redAccent[500]}60`,
                  },
                  "100%": {
                    transform: "scale(1)",
                    boxShadow: `0 8px 24px ${colors.redAccent[500]}40`,
                  },
                },
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 64,
                  color: colors.grey[100],
                }}
              />
            </Box>
          </Box>

          {/* 404 Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "4rem", md: "6rem" },
              fontWeight: 900,
              background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[500]} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 2,
              letterSpacing: "0.1em",
            }}
          >
            404
          </Typography>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              color: colors.grey[100],
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Page Not Found
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              fontSize: "1.1rem",
              mb: 4,
              maxWidth: "500px",
              mx: "auto",
              lineHeight: 1.8,
            }}
          >
            Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or the URL might be incorrect.
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
              sx={{
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                color: "white",
                px: 4,
                py: 1.5,
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
                },
                transition: "all 0.3s ease",
              }}
            >
              Go to Home
            </Button>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: colors.blueAccent[500],
                color: colors.blueAccent[300],
                px: 4,
                py: 1.5,
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                borderWidth: 2,
                "&:hover": {
                  borderColor: colors.blueAccent[400],
                  backgroundColor: `${colors.blueAccent[700]}20`,
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Go Back
            </Button>
          </Box>

          {/* Helpful Links */}
          <Box
            sx={{
              mt: 5,
              pt: 4,
              borderTop: `1px solid ${colors.grey[700]}40`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[400],
                mb: 2,
                fontSize: "0.9rem",
              }}
            >
              You might want to visit:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="text"
                startIcon={<LoginIcon />}
                onClick={() => navigate("/individual-login")}
                sx={{
                  color: colors.blueAccent[300],
                  textTransform: "none",
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: `${colors.blueAccent[700]}20`,
                  },
                }}
              >
                Individual Login
              </Button>
              <Button
                variant="text"
                startIcon={<LoginIcon />}
                onClick={() => navigate("/corporate-login")}
                sx={{
                  color: colors.blueAccent[300],
                  textTransform: "none",
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: `${colors.blueAccent[700]}20`,
                  },
                }}
              >
                Corporate Login
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/about")}
                sx={{
                  color: colors.blueAccent[300],
                  textTransform: "none",
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: `${colors.blueAccent[700]}20`,
                  },
                }}
              >
                About
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/contact")}
                sx={{
                  color: colors.blueAccent[300],
                  textTransform: "none",
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: `${colors.blueAccent[700]}20`,
                  },
                }}
              >
                Contact
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

